/**
 * HowToTopicCard - one "how do I…?" topic on the /how-to guide.
 *
 * Atomic Design role: Molecule. A collapsible card built on the native
 * <details>/<summary> pair, so it opens with a tap, works with the keyboard and
 * screen readers out of the box, and prints/searches (Ctrl+F) without any
 * JavaScript state. Shows:
 *
 *   ▸ Title                         (always visible)
 *     One-line summary              (always visible)
 *     [media]                       (when open, optional — e.g. HowToVideo)
 *     1. Numbered step …            (when open)
 *     💡 Tips                        (when open, optional)
 *
 * Step and tip strings may carry **bold** markers for exact button labels;
 * they are rendered as <strong> by renderGuideText().
 *
 * @example
 * <HowToTopicCard title="Start a job" summary="Open it and tap Start Work." steps={['Tap **Start Work**.']} />
 */
import React from 'react';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

export interface HowToTopicCardProps {
	/** Anchor id so a topic can be linked to (/how-to#start-a-job). */
	id?: string;
	/** "How do I…" title, in plain words. */
	title: string;
	/** One sentence saying what the topic is for. */
	summary: string;
	/** Ordered steps. **text** renders bold. */
	steps: string[];
	/** Optional helpful extras. **text** renders bold. */
	tips?: string[];
	/** Optional slot shown above the steps — the page passes a HowToVideo
	 *  for topics that have a walkthrough. A slot rather than a `video` prop
	 *  so the card stays a plain layout piece. */
	media?: React.ReactNode;
	/** Start expanded (e.g. the first topic, or a deep-linked one). */
	defaultOpen?: boolean;
	/** Optional extra classes on the root. */
	className?: string;
}

/* ============================================
 * HELPERS
 * ============================================ */

/**
 * Render a guide string, turning **bold** spans into <strong>. Deliberately
 * tiny — the guide content is ours, and a full Markdown renderer would be a
 * dependency for one feature.
 */
export function renderGuideText(text: string): React.ReactNode {
	const parts = text.split(/(\*\*[^*]+\*\*)/g);
	return parts.map((part, i) => (part.startsWith('**') && part.endsWith('**')
		? <strong key={i} className="font-semibold text-brand-text">{part.slice(2, -2)}</strong>
		: <React.Fragment key={i}>{part}</React.Fragment>));
}

/* ============================================
 * COMPONENT
 * ============================================ */

export const HowToTopicCard: React.FC<HowToTopicCardProps> = ({ id, title, summary, steps, tips = [], media, defaultOpen = false, className = '' }) => (
	<details id={id} open={defaultOpen} className={`how-to-topic-card group rounded-lg border border-brand-border bg-white scroll-mt-16px ${className}`.trim()} data-ref="molecule-how-to-topic-card">
		{/* The summary row is the whole tap target: chevron, title and one-liner. */}
		<summary className="how-to-topic-card__summary flex items-start gap-12px min-h-44px p-16px cursor-pointer list-none [&::-webkit-details-marker]:hidden">
			<svg className="mt-2px w-20px h-20px shrink-0 text-brand-primary transition-transform group-open:rotate-90" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
				<path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
			</svg>
			<span className="flex flex-col gap-2px min-w-0">
				<span className="text-16px font-sans font-semibold text-brand-text">{title}</span>
				<span className="text-14px font-sans text-brand-text-muted">{summary}</span>
			</span>
		</summary>

		{/* pl-48px lines the body up under the title; on a phone that indent
		    costs a portrait video too much width, so it starts at 16px there. */}
		<div className="how-to-topic-card__body px-16px pb-16px tablet:pl-48px">
			{media}
			<ol className="flex flex-col gap-8px list-decimal pl-20px text-15px font-sans text-brand-text leading-relaxed">
				{steps.map((step, i) => <li key={i}>{renderGuideText(step)}</li>)}
			</ol>

			{tips.length > 0 && (
				<div className="how-to-topic-card__tips mt-12px p-12px rounded-md bg-brand-surface">
					<p className="mb-4px text-13px font-sans font-semibold uppercase tracking-wider text-brand-text-muted">Good to know</p>
					<ul className="flex flex-col gap-4px list-disc pl-20px text-14px font-sans text-brand-text">
						{tips.map((tip, i) => <li key={i}>{renderGuideText(tip)}</li>)}
					</ul>
				</div>
			)}
		</div>
	</details>
);

export default HowToTopicCard;
